// Lightweight analytics helper around the gtag/dataLayer that GTM provides.
//
// The gtag stub + Consent Mode v2 default ("denied") are set inline in
// index.html before GTM loads; GA4 and any marketing pixels are configured as
// tags inside GTM (container GTM-T5BPLK3B). This module only pushes events and
// consent updates onto that dataLayer — a safe no-op if gtag is unavailable.

type GtagFn = (...args: unknown[]) => void

interface AnalyticsWindow extends Window {
  dataLayer?: Record<string, unknown>[]
  gtag?: GtagFn
}

function getGtag(): GtagFn | null {
  if (typeof window === 'undefined') return null
  const w = window as AnalyticsWindow
  return typeof w.gtag === 'function' ? w.gtag : null
}

/**
 * Push a raw object onto the GTM dataLayer. This is the single low-level entry
 * point every event helper goes through. Safe no-op during SSR.
 */
export function pushDataLayer(obj: Record<string, unknown>): void {
  if (typeof window === 'undefined') return
  const w = window as AnalyticsWindow
  w.dataLayer = w.dataLayer || []
  try {
    w.dataLayer.push(obj)
  } catch {
    // Never let analytics break the app.
  }
}

/**
 * Push a named GTM event with flat params, e.g. `{ event, ...params }`.
 * Configure a matching Custom Event trigger + GA4 Event tag in GTM to forward
 * it to GA4. (gtag('event') would NOT reliably fire GTM tags, hence dataLayer.)
 */
export function track(eventName: string, params?: Record<string, unknown>): void {
  pushDataLayer({ event: eventName, ...(params || {}) })
}

/**
 * Log an error to the console and push a non-fatal `exception` event.
 */
export function trackError(error: unknown, context?: string): void {
  const description =
    error instanceof Error ? error.message : typeof error === 'string' ? error : String(error)
  const label = context ? `${context}: ${description}` : description

  // Always surface to the console for local debugging / error consoles.
  console.error('[analytics]', label, error)

  pushDataLayer({ event: 'exception', description: label, fatal: false })
}

type ConsentState = 'granted' | 'denied'

/**
 * Update GA4 Consent Mode v2 state. No-op when gtag is unavailable.
 */
export function setConsent(state: { analytics: ConsentState; ad?: ConsentState }): void {
  const ad = state.ad ?? 'denied'
  const consent = {
    analytics_storage: state.analytics,
    ad_storage: ad,
    ad_user_data: ad,
    ad_personalization: ad,
  }

  // 1. Update Google Consent Mode v2 (gtag stub is created in index.html).
  const gtag = getGtag()
  if (gtag) {
    try {
      gtag('consent', 'update', consent)
    } catch {
      // ignore
    }
  }

  // 2. Push a custom event so GTM triggers can react to the resolved choice.
  //    The consent statuses are carried in the event parameters.
  pushDataLayer({ event: 'consent_update', consent_status: consent })
}
